// Copyright (c) Microgreen Manager LLC. All rights reserved. Proprietary and confidential. Unauthorized copying is strictly prohibited.
import {
  DARK_MODE,
  DEFAULT_DATE_FORMAT,
  type DarkModePreference,
  type DateFormat,
  FREE_TRIAL_EXPIRATION_GRACE_PERIOD_SECONDS,
  SUBSCRIPTION_STATUS,
  SUBSCRIPTION_TIER,
  type SubscriptionStatusType,
  type SubscriptionTierType,
  WEEK_DAY_START,
  WEIGHT_UNITS,
  type WeekStartDayType,
  type WeightUnit,
  utils,
} from '@seedplanner/shared-utils';
import * as React from 'react';
import { useAppContext } from './AppContext';

const UserContext = React.createContext<UserContextType | undefined>(undefined);

interface UserContextType {
  readonly timezone: string;
  readonly permissions: {
    readonly can_edit_orders: boolean;
    readonly can_edit_crops_blends: boolean;
    readonly can_edit_tasks: boolean;
    readonly can_edit_plantings: boolean;
    readonly can_complete_tasks: boolean;
  };
  readonly subscriptionStatus: SubscriptionStatusType;
  readonly subscriptionTier: SubscriptionTierType;
  readonly freeTrialEndsAt: number | null;
  readonly language: string;
  readonly preferredWeightUnit: WeightUnit;
  readonly preferredDateFormat: DateFormat;
  readonly darkModePreference: DarkModePreference;
  readonly weekStartPreference: WeekStartDayType;
  readonly setUserContext: (newUserContext: UserContextValue) => void;
  readonly setUserPreferredWeightUnit: (newWeightUnit: WeightUnit) => void;
  readonly setUserDarkModePreference: (newDarkModePreference: DarkModePreference) => void;
  readonly setUserPreferredDateFormat: (newDateFormat: DateFormat) => void;
  readonly setUserWeekStartPreference: (newWeekStartPreference: WeekStartDayType) => void;
  readonly setUserSubStatusAndTier: ({
    subStatus,
    subTier,
  }: {
    readonly subStatus: SubscriptionStatusType;
    readonly subTier: SubscriptionTierType;
  }) => void;
  readonly resetUserContext: () => void;
}

const defaultUserContext: UserContextValue = {
  timezone: 'America/Chicago',
  permissions: {
    can_edit_orders: false,
    can_edit_crops_blends: false,
    can_edit_tasks: false,
    can_edit_plantings: false,
    can_complete_tasks: false,
  },
  language: 'en-US',
  subscriptionStatus: SUBSCRIPTION_STATUS.TRIALING,
  subscriptionTier: SUBSCRIPTION_TIER.FREE_TRIAL,
  freeTrialEndsAt: null,
  preferredWeightUnit: WEIGHT_UNITS.OUNCES,
  preferredDateFormat: DEFAULT_DATE_FORMAT,
  darkModePreference: DARK_MODE.SYSTEM,
  weekStartPreference: WEEK_DAY_START.SUNDAY,
};

export type UserContextValue = Omit<
  UserContextType,
  | 'setUserContext'
  | 'setUserPreferredWeightUnit'
  | 'setUserDarkModePreference'
  | 'resetUserContext'
  | 'setUserPreferredDateFormat'
  | 'setUserWeekStartPreference'
  | 'setUserSubStatusAndTier'
>;

export type InitialUserState = UserContextValue | undefined;

export const useUserContext = (): UserContextType => {
  const context = React.useContext(UserContext);
  if (context === undefined) {
    throw new Error('useUserContext must be used within a UserContextProvider');
  }
  return context;
};

export const useGating = (): {
  // readonly canUseStarter: boolean;
  readonly canUseBasic: boolean;
  // readonly canUsePro: boolean;
} => {
  const { enableSubscriptionEnforcement } = useAppContext();
  const { subscriptionStatus, freeTrialEndsAt } = useUserContext();
  if (subscriptionStatus === SUBSCRIPTION_STATUS._INTERNAL_FREE_PASS) {
    return { canUseBasic: true };
  }
  if (!enableSubscriptionEnforcement) {
    return { canUseBasic: true };
  }
  // Free trial hasn't started yet
  if (freeTrialEndsAt == null) {
    return { canUseBasic: true };
  }
  // Free trial has ended yet
  const freeTrialHasEnded = freeTrialEndsAt + FREE_TRIAL_EXPIRATION_GRACE_PERIOD_SECONDS <= utils.nowSeconds();
  if (!freeTrialHasEnded) {
    return { canUseBasic: true };
  }
  // Subscription is active/in good standing
  if (subscriptionStatus === SUBSCRIPTION_STATUS.ACTIVE) {
    return { canUseBasic: true };
  }
  return { canUseBasic: false };
};

export function UserContextProvider({
  initialState,
  children,
}: {
  readonly initialState: InitialUserState;
  readonly children: React.ReactNode;
}) {
  const [userContext, setUserContext] = React.useState(initialState ?? defaultUserContext);
  const setUserPreferredWeightUnit = (newWeightUnit: WeightUnit) => {
    setUserContext({ ...userContext, preferredWeightUnit: newWeightUnit });
  };
  const setUserDarkModePreference = (newDarkModePreference: DarkModePreference) => {
    setUserContext({ ...userContext, darkModePreference: newDarkModePreference });
  };
  const setUserPreferredDateFormat = (newDateFormat: DateFormat) => {
    setUserContext({ ...userContext, preferredDateFormat: newDateFormat });
  };
  const setUserWeekStartPreference = (newWeekStartPreference: WeekStartDayType) => {
    setUserContext({ ...userContext, weekStartPreference: newWeekStartPreference });
  };
  // Typically set together anyway. But must be set together to avoid overriding each other
  const setUserSubStatusAndTier = ({
    subStatus,
    subTier,
  }: {
    readonly subStatus: SubscriptionStatusType;
    readonly subTier: SubscriptionTierType;
  }) => {
    setUserContext({
      ...userContext,
      subscriptionStatus: subStatus,
      subscriptionTier: subTier,
    });
  };
  const resetUserContext = () => {
    setUserContext(defaultUserContext);
  };
  return (
    <UserContext.Provider
      value={{
        ...userContext,
        setUserContext,
        setUserPreferredWeightUnit,
        setUserDarkModePreference,
        setUserPreferredDateFormat,
        setUserWeekStartPreference,
        setUserSubStatusAndTier,
        resetUserContext,
      }}
    >
      {children}
    </UserContext.Provider>
  );
}
